import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, fetchReportsSummary, fetchProjectsList, exportMonthlyPayrollAllocationXlsx } from '../api';
import { formatDateDMY, formatMinutesToHM } from '../ui/format';
import ReportTable from '../components/reports/ReportTable.jsx';
import { computeUtilizationPct, overbookedFlag, sortRows } from '../utils/reports';
import DatePicker from '../ui/DatePicker.jsx';
import SegmentedControl from '../components/SegmentedControl.jsx';
import Card from '../ui/Card.jsx';
import HoursTrendChart from '../components/charts/HoursTrendChart.jsx';
import UtilizationByProjectChart from '../components/charts/UtilizationByProjectChart.jsx';
import WorkloadByPersonChart from '../components/charts/WorkloadByPersonChart.jsx';
import { getActiveTheme, resolveThemeName } from '../ui/tokens';

function getWeekRange(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getUTCDay();
  const mondayOffset = (day + 6) % 7; // 0..6
  const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - mondayOffset + offsetWeeks * 7));
  const sunday = new Date(Date.UTC(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate() + 6));
  const toY = (d) => d.toISOString().slice(0, 10);
  return { from: toY(monday), to: toY(sunday) };
}

function getMonthRange(offsetMonths = 0) {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offsetMonths, 1));
  const nextFirst = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offsetMonths + 1, 1));
  const last = new Date(nextFirst.getTime() - 24*3600*1000);
  const toY = (d) => d.toISOString().slice(0, 10);
  return { from: toY(first), to: toY(last) };
}

function normalizePersonId(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === 'all' || lower === 'null' || lower === 'undefined') return null;
  return s;
}

export default function Reports() {
  const [role, setRole] = useState('employee');
  const [projects, setProjects] = useState([]);
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [range, setRange] = useState(() => { try { return localStorage.getItem('reports.range') || 'this_week'; } catch { return 'this_week'; } });
  const initial = useMemo(() => getWeekRange(0), []);
  const [from, setFrom] = useState(() => { try { return localStorage.getItem('reports.from') || initial.from; } catch { return initial.from; } });
  const [to, setTo] = useState(() => { try { return localStorage.getItem('reports.to') || initial.to; } catch { return initial.to; } });
  const [managerId, setManagerId] = useState(() => { try { return localStorage.getItem('reports.managerId') || ''; } catch { return ''; } });
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('reports.tab') || 'project'; } catch { return 'project'; } }); // project | person | day
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [exportingPayroll, setExportingPayroll] = useState(false);
  const [drawerCtx, setDrawerCtx] = useState(null); // { type: 'project'|'person'|'day', project_id|user_id|day, name }
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerTab, setDrawerTab] = useState('tasks');
  const [personId, setPersonId] = useState(() => {
    try {
      const v = localStorage.getItem('reports.personId');
      return normalizePersonId(v);
    } catch {
      return null;
    }
  });
  // Breakdown (By Day) local UI state
  const [search, setSearch] = useState('');
  const [onlyTop, setOnlyTop] = useState(false);
  const [sortDay, setSortDay] = useState({ key: 'date', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const j = await res.json();
          if (!cancelled) setRole(j.user?.role || 'employee');
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const roleLower = String(role || '').trim().toLowerCase();
  const isManagerLike = roleLower === 'manager' || roleLower === 'junior_manager' || roleLower === 'admin';
  const canExportPayroll = roleLower === 'manager' || roleLower === 'admin';
 
  // Load projects list for filter options
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchProjectsList();
        if (!cancelled) setProjects(rows);
      } catch (_) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // React to quick range changes by updating from/to
  useEffect(() => {
    let next;
    if (range === 'this_week') next = getWeekRange(0);
    else if (range === 'last_week') next = getWeekRange(-1);
    else if (range === 'this_month') next = getMonthRange(0);
    else if (range === 'last_month') next = getMonthRange(-1);
    if (next) { setFrom(next.from); setTo(next.to); }
    try { localStorage.setItem('reports.range', range); } catch {}
  }, [range]);

  // Persist filters
  useEffect(() => { try { localStorage.setItem('reports.from', from || ''); } catch {} }, [from]);
  useEffect(() => { try { localStorage.setItem('reports.to', to || ''); } catch {} }, [to]);
  useEffect(() => { try { localStorage.setItem('reports.managerId', managerId || ''); } catch {} }, [managerId]);
  useEffect(() => { try { localStorage.setItem('reports.tab', tab || 'project'); } catch {} }, [tab]);
  useEffect(() => { try { localStorage.setItem('reports.personId', normalizePersonId(personId) || ''); } catch {} }, [personId]);

  // Auto-fetch summary when filters change (debounced)
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true); setError('');
      try {
        if (isManagerLike) {
          const [summary, optionsSummary] = await Promise.all([
            fetchReportsSummary({
              from,
              to,
              managerId,
              role: 'manager',
              personId: normalizePersonId(personId),
            }),
            fetchReportsSummary({
              from,
              to,
              managerId,
              role: 'manager',
              personId: null,
            }),
          ]);
          if (!cancelled) {
            setData(summary);
            setPeopleOptions(optionsSummary?.byPerson || []);
          }
        } else {
          const summary = await fetchReportsSummary({
            from,
            to,
            role: 'employee',
          });
          if (!cancelled) {
            setData(summary);
            setPeopleOptions([]);
          }
        }
      } catch (e) {
        if (!cancelled) { setData(null); setPeopleOptions([]); setError(e?.message || 'Failed to load reports'); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [from, to, managerId, role, personId]);

  

    const ByDay = () => {
      let rows = (data?.byDay || []).map(d => ({
        id: d.date,
        label: formatDateDMY(d.date),
        date: d.date,
        planned_minutes: Number(d.planned_minutes || 0),
        logged_assigned_minutes: Number(d.logged_assigned_minutes || 0),
        logged_other_minutes: Number(d.logged_other_minutes || 0),
        logged_total_minutes: Number(d.logged_total_minutes || 0),
        utilization_pct: computeUtilizationPct(d.planned_minutes, d.logged_total_minutes),
        over_8h: overbookedFlag(d.logged_total_minutes),
      }));
      if (onlyTop) rows = rows.filter(r => (r.planned_minutes + r.logged_total_minutes) > 0);
      if (search) rows = rows.filter(r => r.label.toLowerCase().includes(search.toLowerCase()));
      rows = sortRows(rows, sortDay.key, sortDay.dir);

      const columns = [
        { label: 'Day', sortKey: 'date', defaultDir: 'desc', render: (r)=> r.label },
        { label: 'Planned', sortKey: 'planned_minutes', align: 'right', render: (r)=> formatMinutesToHM(r.planned_minutes) },
        { label: 'Logged (P|O)', sortKey: 'logged_assigned_minutes', align: 'right', render: (r)=> (
          <span> P {formatMinutesToHM(r.logged_assigned_minutes)} • O {formatMinutesToHM(r.logged_other_minutes)}</span>
        )},
        { label: 'Total', sortKey: 'logged_total_minutes', align: 'right', render: (r)=> formatMinutesToHM(r.logged_total_minutes) },
        { label: 'Over 8h?', sortKey: 'over_8h', align: 'right', render: (r)=> (
          <span className={`px-2 py-0.5 text-xs rounded ${r.over_8h ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>{r.over_8h ? 'Yes' : 'No'}</span>
        )},
      ];

      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input value={search} onChange={e=> setSearch(e.target.value)} placeholder="Search day" className="border rounded px-2 py-1 text-sm" />
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={onlyTop} onChange={e=> setOnlyTop(e.target.checked)} /> Top priority only</label>
          </div>
          <ReportTable
            columns={columns}
            rows={rows}
            sortState={sortDay}
            onSortChange={setSortDay}
            onRowClick={(r)=> setDrawerCtx({ type: 'day', day: r.id, name: r.label })}
            emptyText="No data for selected range"
          />
        </div>
        );
      };

    const KPI = ({ label, value }) => (
      <Card
        className={`flex-1 min-w-[160px] ${(resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium') ? '' : 'bg-gradient-to-r from-indigo-50 via-pink-50 to-amber-50 dark:from-slate-800 dark:via-fuchsia-900/30 dark:to-amber-900/30'}`}
      >
        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</div>
        <div className="text-lg font-semibold">{value}</div>
      </Card>
    );

  const Header = () => {
    const k = data?.kpis || { planned_minutes:0, logged_assigned_minutes:0, logged_other_minutes:0, logged_total_minutes:0, utilization_pct:0, overbooked_days: 0 };
    const utilization = `${k.utilization_pct || 0}%`;
    const overbookedDays = Number(k.overbooked_days || 0);
    return (
      <div className="flex flex-wrap gap-2 mb-3">
        <KPI label="Planned" value={formatMinutesToHM(k.planned_minutes)} />
        <KPI label="Logged (Planned)" value={formatMinutesToHM(k.logged_assigned_minutes)} />
        <KPI label="Logged (Other)" value={formatMinutesToHM(k.logged_other_minutes)} />
        <KPI label="Logged (Total)" value={formatMinutesToHM(k.logged_total_minutes)} />
        <KPI label="Utilization %" value={utilization} />
        <KPI label="Overbooked days" value={String(overbookedDays)} />
      </div>
    );
  };

  const FilterBar = () => (
    <div className="flex flex-wrap items-end gap-3 mb-4 sticky top-2 z-10">
      <div className="flex items-center gap-2">
        <SegmentedControl
          ariaLabel="Quick ranges"
          options={[
            { key: 'last_week', label: 'Last week' },
            { key: 'this_week', label: 'This week' },
            { key: 'last_month', label: 'Last month' },
            { key: 'this_month', label: 'This month' },
          ]}
          value={range}
          onChange={setRange}
          size="sm"
        />
      </div>
      <div className="flex items-center gap-2 text-sm">
        <DatePicker label="From" value={from} onChange={setFrom} />
        <DatePicker label="To" value={to} onChange={setTo} />
      </div>
      {isManagerLike && (
        <>
          <div className="flex items-center gap-1 text-sm">
            <label className="text-xs text-gray-500">Manager</label>
            <select value={managerId} onChange={e=>setManagerId(e.target.value)} className="border rounded px-2 py-1 text-sm min-w-[180px]">
              <option value="">All managers</option>
              {(() => {
                const byId = new Map();
                for (const p of (projects || [])) {
                  const id = p?.manager_id || p?.manager_user_id;
                  if (!id) continue;
                  const label = p?.manager_display_name
                    || p?.manager_name
                    || [p?.manager_first_name, p?.manager_last_name].filter(Boolean).join(' ').trim()
                    || p?.manager_email
                    || null;
                  if (!label) continue;
                  if (!byId.has(String(id))) byId.set(String(id), String(label));
                }
                const options = Array.from(byId.entries()).map(([id, label]) => ({ id, label }));
                options.sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
                const hasSelected = managerId && options.some(o => String(o.id) === String(managerId));
                return (
                  <>
                    {managerId && !hasSelected && <option value={managerId}>Selected manager</option>}
                    {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </>
                );
              })()}
            </select>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <label className="text-xs text-gray-500">Person</label>
            <select
              value={normalizePersonId(personId) ?? ''}
              onChange={e => {
                const v = e.target.value;
                setPersonId(normalizePersonId(v));
              }}
              className="border rounded px-2 py-1 text-sm min-w-[180px]"
            >
              <option value="">All people</option>
              {(() => {
                const normalized = normalizePersonId(personId);
                const base = Array.isArray(peopleOptions) ? peopleOptions : [];
                const options = base.slice().sort((a,b)=> String(a.display_name||a.name||'').localeCompare(String(b.display_name||b.name||'')));
                const hasSelected = !!(normalized && options.some(p => String(p.user_id) === String(normalized)));
                return (
                  <>
                    {normalized && !hasSelected && <option value={normalized}>Selected person (no data)</option>}
                    {options.map(p => (
                      <option key={p.user_id} value={p.user_id}>{p.display_name || p.name || '—'}</option>
                    ))}
                  </>
                );
              })()}
            </select>
          </div>
        </>
      )}
      {/* Apply and export controls removed; auto-fetch is enabled, exports moved to header */}
    </div>
  );

  // Breakdown tabs selector
  const Tabs = () => (
    <div className="mb-3">
      <SegmentedControl
        ariaLabel="Reports tabs"
        options={[
          { key: 'project', label: 'By Project' },
          ...(isManagerLike ? [{ key: 'person', label: 'By Person' }] : []),
          { key: 'day', label: 'By Day' },
        ]}
        value={tab}
        onChange={setTab}
      />
    </div>
  );

  // Charts section
  const Charts = () => (
    <div className="grid gap-3 md:grid-cols-2">
      <HoursTrendChart
        data={data?.byDay || []}
        loading={loading}
        onDayClick={(date)=> {
          setDrawerCtx({ type: 'day', day: date, name: formatDateDMY(date) });
        }}
      />
      <UtilizationByProjectChart
        projects={data?.byProject || []}
        loading={loading}
        onProjectClick={(pid)=> {
          const proj = (data?.byProject||[]).find(p => p.project_id === pid);
          setDrawerCtx({ type: 'project', project_id: pid, name: proj?.project_name || 'Unassigned' });
        }}
      />
      <div className="md:col-span-2">
        <WorkloadByPersonChart
          people={data?.byPerson || []}
          selectedUserId={personId}
          loading={loading}
          onPersonClick={(uid)=> {
            const u = (data?.byPerson||[]).find(x => x.user_id === uid);
            setTab('person');
            setPersonId(uid);
            setDrawerCtx({ type: 'person', user_id: uid, name: u?.display_name || '—' });
          }}
        />
      </div>
    </div>
  );

  // Drawer data loader (separate endpoint, shows loading)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!drawerCtx) return;
      setDrawerLoading(true); setDrawerData(null);
      try {
        const params = new URLSearchParams({ from, to });
        if (drawerCtx.type === 'project') params.set('project_id', String(drawerCtx.project_id));
        if (drawerCtx.type === 'person') params.set('user_id', String(drawerCtx.user_id));
        if (drawerCtx.type === 'day') params.set('day', drawerCtx.day);
        // Respect current filters in drilldown
        if (personId && drawerCtx.type !== 'person') params.set('person_id', String(personId));
        if (managerId && drawerCtx.type !== 'project') params.set('manager_id', String(managerId));
        const token = localStorage.getItem('token');
        const url = `${API_BASE_URL}/api/reports/drilldown?${params.toString()}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to load drilldown');
        const j = await res.json();
        if (!cancelled) setDrawerData(j);
      } catch (e) {
        if (!cancelled) setDrawerData(null);
      } finally {
        if (!cancelled) setDrawerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [drawerCtx, from, to, personId, managerId]);

  const Drawer = () => {
    if (!drawerCtx) return null;
    const title = drawerCtx.type === 'project' ? `Project — ${drawerCtx.name}`
      : drawerCtx.type === 'person' ? `Person — ${drawerCtx.name}`
      : `Day — ${drawerCtx.name}`;
    const k = drawerData?.kpis || { planned_minutes:0, logged_assigned_minutes:0, logged_other_minutes:0, logged_total_minutes:0, utilization_pct:0, overbooked_days:0 };
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[6px]" onClick={()=>{ setDrawerCtx(null); setDrawerData(null); }} />
        <div className="absolute right-4 top-4 w-[800px] max-w-[96vw] bg-white dark:bg-gray-900 rounded shadow-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
            <div className="text-lg font-semibold">{title}</div>
            <button onClick={()=>{ setDrawerCtx(null); setDrawerData(null); }} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
          </div>
          {drawerLoading && (<div className="text-xs text-gray-600 dark:text-gray-400">Loading…</div>)}
          {!drawerLoading && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm mb-3">
                <Card><div className="text-xs text-gray-600 dark:text-gray-400">Planned</div><div className="font-semibold">{formatMinutesToHM(k.planned_minutes)}</div></Card>
                <Card><div className="text-xs text-gray-600 dark:text-gray-400">Logged (Planned)</div><div className="font-semibold">{formatMinutesToHM(k.logged_assigned_minutes)}</div></Card>
                <Card><div className="text-xs text-gray-600 dark:text-gray-400">Logged (Other)</div><div className="font-semibold">{formatMinutesToHM(k.logged_other_minutes)}</div></Card>
                <Card><div className="text-xs text-gray-600 dark:text-gray-400">Total</div><div className="font-semibold">{formatMinutesToHM(k.logged_total_minutes)}</div></Card>
                <Card><div className="text-xs text-gray-600 dark:text-gray-400">Utilization %</div><div className="font-semibold">{String(k.utilization_pct || 0)}%</div></Card>
                <Card><div className="text-xs text-gray-600 dark:text-gray-400">Overbooked days</div><div className="font-semibold">{String(k.overbooked_days || 0)}</div></Card>
              </div>
              <div>
                <SegmentedControl ariaLabel="Drawer tabs" options={[
                  { key: 'tasks', label: 'Tasks' },
                  ...(drawerCtx.type !== 'person' ? [{ key: 'people', label: 'People' }] : []),
                  ...(drawerCtx.type !== 'day' ? [{ key: 'days', label: 'Days' }] : []),
                ]} value={drawerTab} onChange={setDrawerTab} size="sm" />
              </div>
              <div className="mt-2">
                {drawerTab === 'tasks' && (
                  <div className="grid gap-1">
                    {(drawerData?.tasks||[]).map((t,i)=> {
                      const totalLogged = Number(t.logged_assigned_minutes||0) + Number(t.logged_other_minutes||0);
                      return (
                        <div key={i} className="border rounded p-2 text-xs flex flex-col gap-1">
                          <div className="flex justify-between">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{t.job_name || `Job ${t.job_id}`} • {t.task_name || (t.task_id ? `Task ${t.task_id}` : '—')}</div>
                            </div>
                            <div className="whitespace-nowrap">Planned {formatMinutesToHM(t.planned_minutes)}</div>
                          </div>
                          <div className="flex justify-between text-[11px] text-gray-600 dark:text-gray-300">
                            <div>Logged P {formatMinutesToHM(t.logged_assigned_minutes)} • O {formatMinutesToHM(t.logged_other_minutes)} • Total {formatMinutesToHM(totalLogged)}</div>
                            <div className="flex flex-wrap gap-1">
                              {(t.owners||[]).map(o => (<span key={o.id} className="px-1.5 py-0.5 border rounded-full">{o.name || '—'}</span>))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {(drawerData?.tasks||[]).length===0 && <div className="text-xs text-gray-600 dark:text-gray-400">No tasks</div>}
                  </div>
                )}
                {drawerTab === 'people' && (
                  <div className="grid gap-1">
                    {(drawerData?.people||[]).map((p,i)=> (
                      <div key={i} className="border rounded p-2 text-xs flex justify-between">
                        <div>User {p.user_id}</div>
                        <div>{formatMinutesToHM(p.logged_total_minutes)} (P {formatMinutesToHM(p.logged_assigned_minutes)} | O {formatMinutesToHM(p.logged_other_minutes)})</div>
                      </div>
                    ))}
                    {(drawerData?.people||[]).length===0 && <div className="text-xs text-gray-600 dark:text-gray-400">No people</div>}
                  </div>
                )}
                {drawerTab === 'days' && (
                  <div className="grid gap-1">
                    {(drawerData?.days||[]).map((d,i)=> (
                      <div key={i} className="border rounded p-2 text-xs flex justify-between">
                        <div>{formatDateDMY(typeof d.date === 'string' ? d.date : (new Date(d.date)).toISOString().slice(0,10))}</div>
                        <div>{formatMinutesToHM(d.logged_total_minutes)} (P {formatMinutesToHM(d.logged_assigned_minutes)} | O {formatMinutesToHM(d.logged_other_minutes)})</div>
                      </div>
                    ))}
                    {(drawerData?.days||[]).length===0 && <div className="text-xs text-gray-600 dark:text-gray-400">No days</div>}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const ByProject = () => {
    const rows = data?.byProject || [];
    return (
      <div className="grid gap-3">
        {rows.map(r => {
          const utilPct = r.planned_minutes > 0 ? Math.round((r.logged_total_minutes * 100) / r.planned_minutes) : 0;
          return (
            <div key={String(r.project_id)} className="border rounded p-3 bg-white dark:bg-black cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900"
                 onClick={()=> setDrawerCtx({ type: 'project', project_id: r.project_id, name: r.project_name || 'Unassigned' })}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{r.project_name || 'Unassigned'}</div>
                  <div className="text-xs text-gray-500">{r.client_name || '—'}</div>
                </div>
                <div className="text-sm">{utilPct}%</div>
              </div>
              <div className="text-xs mt-2 text-gray-600 dark:text-gray-300">
                Planned {formatMinutesToHM(r.planned_minutes)} • Logged {formatMinutesToHM(r.logged_total_minutes)} (P {formatMinutesToHM(r.logged_assigned_minutes)} | O {formatMinutesToHM(r.logged_other_minutes)})
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (<div className="text-sm text-gray-600 dark:text-gray-400">No data for selected range</div>)}
      </div>
    );
  };
  const ByPerson = () => {
    const all = data?.byPerson || [];
    const pid = personId != null ? (typeof personId === 'string' ? Number(personId) : personId) : null;
    const rows = pid ? all.filter(u => String(u.user_id) === String(pid)) : all;
    return (
      <div className="grid gap-3">
        {rows.map(u => (
          <div key={u.user_id} className="border rounded p-3 bg-white dark:bg-black">
            <div className="font-medium">{u.display_name || '—'}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
              Planned {formatMinutesToHM(u.planned_minutes)} • Logged {formatMinutesToHM(u.logged_total_minutes)} (P {formatMinutesToHM(u.logged_assigned_minutes)} | O {formatMinutesToHM(u.logged_other_minutes)})
            </div>
            {(u.projects||[]).length>0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {(u.projects||[]).slice(0,5).map((p,i) => (
                  <span key={i} className="px-2 py-0.5 text-xs border rounded-full">{(p.project_name||'—')} • {formatMinutesToHM(p.logged_total_minutes)}</span>
                ))}
              </div>
            )}
          </div>
        ))}
        {personId && rows.length === 0 && (<div className="text-sm text-gray-600 dark:text-gray-400">No data for selected person</div>)}
        {personId && (
          <div className="mt-1">
            <button className="px-2 py-1 text-xs rounded border" onClick={()=> setPersonId(null)}>Clear person filter</button>
          </div>
        )}
      </div>
    );
  };

  

  return (
    <div className="p-4">
      {/* Header row: title + mode chip + exports */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold">Reports</h2>
          <span className={`text-xs px-2 py-0.5 rounded border ${role==='manager' ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50 border-gray-300 text-gray-700'}`}
                title={role==='manager' ? 'Showing team (manager) summary' : 'Showing your personal summary'}>
            Mode: {role==='manager' ? 'Manager' : 'My'}
          </span>
        </div>
        {canExportPayroll && (
          <div className="flex items-center gap-2">
            <button
              disabled={exportingPayroll}
              title="Includes sheets: Payroll Allocation, Payroll Details, Client Summary, Manager Summary"
              onClick={async () => {
                const month = (from || '').slice(0, 7);
                if (!/^\d{4}-\d{2}$/.test(month)) {
                  setError('Please select a month before exporting payroll allocation.');
                  return;
                }
                setExportingPayroll(true);
                try {
                  await exportMonthlyPayrollAllocationXlsx(month);
                } catch (e) {
                  setError(e?.message || 'Payroll allocation export failed');
                } finally {
                  setExportingPayroll(false);
                }
              }}
              className={`px-3 py-1.5 text-sm rounded border ${exportingPayroll ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {exportingPayroll ? 'Exporting…' : 'Export Payroll Allocation (.xlsx)'}
            </button>
          </div>
        )}
      </div>

      <FilterBar />
      {loading && <div className="text-sm text-gray-500">Loading…</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
      {data && <Header />}

      {/* Charts */}
      {data && <>
        <Charts />
        {personId && (() => {
          const u = (data.byPerson||[]).find(x => x.user_id === personId);
          if (!u) return null;
          const util = u.planned_minutes > 0 ? `${Math.round((u.logged_total_minutes*100)/u.planned_minutes)}%` : '0%';
          return (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
              <KPI label="Planned (person)" value={formatMinutesToHM(u.planned_minutes)} />
              <KPI label="Logged P" value={formatMinutesToHM(u.logged_assigned_minutes)} />
              <KPI label="Logged O" value={formatMinutesToHM(u.logged_other_minutes)} />
              <KPI label="Total" value={formatMinutesToHM(u.logged_total_minutes)} />
              <KPI label="Utilization" value={util} />
            </div>
          );
        })()}
      </>}

      {/* Breakdown tabs */}
      <div className="mt-4">
        <Tabs />
        {!data ? (
          <div className="text-sm text-gray-500">No data for selected range</div>
        ) : tab==='project' ? <ByProject /> : tab==='person' ? <ByPerson /> : <ByDay />}
      </div>

      {/* Drawer */}
      <Drawer />
    </div>
  );
}
